import { useEffect, useRef } from "react";
import { trackAdImpression, type AdPlacement } from "@/lib/ad-tracking";
import { recordImpression } from "@/lib/ad-frequency";

/**
 * Tracks an impression when at least ~50% of the element is visible for a moment.
 * Falls back to a plain mount-impression if IntersectionObserver is unavailable.
 */
export function useAdImpression<T extends HTMLElement>(
  affiliateId: string | undefined | null,
  placement: AdPlacement | string,
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!affiliateId) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      trackAdImpression(affiliateId, placement);
      recordImpression(affiliateId);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            trackAdImpression(affiliateId, placement);
            recordImpression(affiliateId);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [affiliateId, placement]);

  return ref;
}
